import {
  dispatchPopover,
  hideCanvasSelectLabel,
  isCanvasSelectLabelTarget,
  showCanvasSelectLabel,
  ROW_TYPES_WITHOUT_POPOVER,
} from "./canvasSelectLabel";
import { canvasObjectTarget } from "./canvasObjectTarget";

const COLUMN_POSITIONS = { first: 1, second: 2, third: 3, fourth: 4 };

export function attachObjectCanvasDelegation(
  canvas,
  getSection,
  getStep,
  getSectionColumn,
  getElement
) {
  if (!canvas) return () => {};

  const resolve = (target) => {
    const resolved = canvasObjectTarget(canvas, target);
    const node = resolved?.target;
    if (!node) return null;
    const type = resolved.type;
    let object;
    let detail;
    if (type === "elements") {
      object = getElement?.(node.getAttribute("data-element-id"));
      if (!object || object.element_type === "row") return null;
      detail = { label: object.element_type, tab: "Content" };
    } else if (type === "steps") {
      object = getStep(node.getAttribute("data-step-id"));
      detail = { label: "Funnel Step" };
    } else if (type === "section_columns") {
      object = getSectionColumn?.(node.getAttribute("data-section-column-id"));
      detail = { label: `Column ${object?.position || object?.id}` };
    } else {
      object = getSection(node.getAttribute("data-section-id"));
      const column =
        type === "columns"
          ? node.getAttribute("data-column-position")
          : undefined;
      detail = {
        label: column
          ? `${object?.element_id ? "Row" : "Section"} Col ${
              COLUMN_POSITIONS[column] || column
            }`
          : object?.element_id
          ? "Row"
          : "Section",
        tab: column ? "Columns" : object?.element_id ? "Row" : "Section",
        column,
        collapse: column,
        popover: !ROW_TYPES_WITHOUT_POPOVER.includes(
          object?.element?.element_type
        ),
      };
    }
    if (!object || object.toBeDeleted) return null;
    return {
      ...detail,
      object_type: type === "columns" ? "sections" : type,
      object_id: object.id,
      object,
      target: node,
    };
  };

  const onClick = (event) => {
    const creation = event.target.closest?.(
      '.cf-draggable-creation-hover[data-object-type="Elements"]'
    );
    if (creation) {
      const container = creation.closest(".cf-cta-item-container");
      if (container) {
        dispatchCustomEvent("newObject", {
          object_type: "Elements",
          section_id: container.getAttribute("data-section-id"),
          column: container.getAttribute("data-section-column"),
        });
      }
      return;
    }
    const detail = resolve(event.target);
    if (!detail || detail.object_type === "elements") return;
    dispatchCustomEvent("selectObject", {
      object_type: detail.object_type,
      object_id: detail.object_id,
      ...(detail.tab ? { tab: detail.tab } : {}),
      ...(detail.column ? { collapse: detail.column } : {}),
      panel_closed: false,
    });
  };

  const onContextMenu = (event) => {
    const detail = resolve(event.target);
    if (!detail || detail.object_type === "elements") return;
    event.preventDefault();
    if (detail.popover === false) return;
    dispatchPopover({
      ...detail,
      anchorPosition: { x: event.clientX, y: event.clientY },
    });
  };

  let hovered = null;
  let occupiedColumn = null;

  const setOccupiedColumn = (column) => {
    if (column === occupiedColumn) return;
    for (const [node, display] of [
      [occupiedColumn, "none"],
      [column, "block"],
    ]) {
      node
        ?.querySelectorAll(
          `.section-add-element[data-section-id='${node.getAttribute(
            "data-section-id"
          )}']`
        )
        .forEach((icon) => {
          icon.style.display = display;
        });
    }
    occupiedColumn = column;
  };

  const clearHover = () => {
    hovered?.target.classList.remove("canvas-hover");
    hovered = null;
  };

  const onHide = (event) => {
    if (event.detail?.source && event.detail.source !== hovered?.object_type)
      return;
    clearHover();
  };

  const updateHover = (target) => {
    if (isCanvasSelectLabelTarget(target)) return;
    setOccupiedColumn(
      target?.nodeType && canvas.contains(target)
        ? target.closest?.(".cf-column, .cf-flex-column")
        : null
    );
    const next =
      canvas.classList.contains("dragging") || target?.closest?.(".spacing")
        ? null
        : resolve(target);
    if (next?.target === hovered?.target) return;
    clearHover();
    hovered = next;
    if (hovered) {
      hovered.target.classList.add("canvas-hover");
      showCanvasSelectLabel(hovered);
    } else {
      hideCanvasSelectLabel();
    }
  };

  const onMouseOver = (event) => updateHover(event.target);
  const onMouseOut = (event) => {
    if (!canvas.contains(event.relatedTarget)) updateHover(event.relatedTarget);
  };

  canvas.addEventListener("click", onClick);
  canvas.addEventListener("contextmenu", onContextMenu);
  canvas.addEventListener("mouseover", onMouseOver);
  canvas.addEventListener("mouseout", onMouseOut);
  document.addEventListener("hideCanvasSelectLabel", onHide);

  return () => {
    clearHover();
    setOccupiedColumn(null);
    canvas.removeEventListener("click", onClick);
    canvas.removeEventListener("contextmenu", onContextMenu);
    canvas.removeEventListener("mouseover", onMouseOver);
    canvas.removeEventListener("mouseout", onMouseOut);
    document.removeEventListener("hideCanvasSelectLabel", onHide);
  };
}
